import { useEffect, useMemo, useRef, useState } from "react";
import { axiosRequestData } from "../../helpers/axios";
import { ChatMessage, SendbirdChatProps, SendbirdUser } from "./chat";
import { getSendbird } from "./sendbird";
import type { GroupChannel, SendbirdGroupChat } from "@sendbird/chat/groupChannel";
import { GroupChannelHandler, MemberState, Member } from "@sendbird/chat/groupChannel";
import { BaseMessage, FileMessage, MessageTypeFilter, UserMessage } from "@sendbird/chat/message";

export const fetchChannelsMetadata = async (
    sb: SendbirdGroupChat,
    channelUrls: string[],
    metaKeys: string[]
): Promise<Record<string, Record<string, string>>> => {
    const results = await Promise.allSettled(
        channelUrls.map(async (url) => {
            const channel = await sb.groupChannel.getChannel(url);
            const metadata = await channel.getMetaData(metaKeys);
            return [url, metadata] as const;
        })
    );
    return Object.fromEntries(
        results
            .filter((r): r is PromiseFulfilledResult<readonly [string, Record<string, string>]> => r.status === 'fulfilled')
            .map(r => r.value)
    );
};

export const getUser = async (skip: boolean = false) => {
    return axiosRequestData("POST", "/wp-json/pg/v1/vip/get_user", {
        skip_boarding: skip,
    });
};

export const useSendbirdUser = (props: SendbirdChatProps) => {
    const [sb, setSb] = useState<SendbirdGroupChat | null>(null);
    const [user, setUser] = useState<SendbirdUser>();
    const [sendbirdMessages, setSendbirdMessages] = useState<ChatMessage[]>([]);
    const [userLoading, setUserLoading] = useState(true);
    const [userError, setUserError] = useState<Error | null>(null);
    const [numberOfVideos, setNumberOfVideos] = useState<number>(0);
    const [channel, setChannel] = useState<GroupChannel | null>(null);
    const [coach, setCoach] = useState<Member | null>(null);
    const [isAnyMessageSent, setIsAnyMessageSent] = useState(false);
    const setCoachRef = useRef(props.setCoach);
    const setAnyMessageSentRef = useRef(props.setAnyMessageSent);
    
    // Initialize Sendbird instance
    useEffect(() => {
        let cancelled = false;
        
        getSendbird()
            .then((instance) => {
                if (!cancelled) setSb(instance);
            })
            .catch((err) => {
                console.error("Failed to initialize Sendbird:", err);
                if (!cancelled) setUserError(err instanceof Error ? err : new Error(String(err)));
            });
        
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setUserLoading(true);
        setUserError(null);
        if (props.user) {
            setUser(props.user);
            setUserLoading(false);
            return;
        }
        getUser()
            .then((res) => {
                if (!cancelled) setUser(res.data as SendbirdUser);
            })
            .catch((err: unknown) => {
                if (!cancelled) setUserError(err instanceof Error ? err : new Error(String(err)));
            })
            .finally(() => {
                if (!cancelled) setUserLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!user || !sb) return;

        let cancelled = false;
        const handlerId = `chat-handler-${user.user_id}`;

        const loadInitialMessages = async () => {
            try {
                await sb.connect(user.user_id, user.access_token);
                const channel: GroupChannel = await sb.groupChannel.getChannel(user.channel_url);
                const query = channel.createPreviousMessageListQuery({
                    limit: 100,
                    reverse: true,
                });
                const messages = await query.load();
                if (cancelled) return;
                setSendbirdMessages(mapToChatMessage(messages));
            } catch (err) {
                console.error("initial load error", err);
            }
        };
        
        const handler = new GroupChannelHandler({
            onMessageReceived: (liveChannel, message) => {
                if (liveChannel.url !== user.channel_url) return;
                setSendbirdMessages((prev) => [
                    mapToChatMessage([message])[0], 
                    ...prev
                ]);
                getCoach(channel!);
            },
        });

        loadInitialMessages();
        sb.groupChannel.addGroupChannelHandler(handlerId, handler);
        const videoMetaKey = (user.user_role === 'coach' || window.pgUser?.is_coach)
            ? 'coach_video_num'
            : 'user_video_num';
        getUserVideoNum(user, videoMetaKey)
            .then((num) => {
                if (!cancelled) setNumberOfVideos(num);
            })
            .catch((err) => {
                console.error("Failed to get user video num:", err);
            });
        
        return () => {
            cancelled = true;
            sb.groupChannel.removeGroupChannelHandler(handlerId);
        };
    }, [user, sb, setSendbirdMessages]);

    useEffect(() => {
        setCoachRef.current = props.setCoach;
        setAnyMessageSentRef.current = props.setAnyMessageSent;
    }, [props.setCoach, props.setAnyMessageSent]);

    useEffect(() => {
        if (isAnyMessageSent || !user?.user_id) return;

        const hasSentMessage = sendbirdMessages.some(m => m.sender_id === user.user_id);
        if (hasSentMessage) {
            setIsAnyMessageSent(true);
        }
    }, [sendbirdMessages, isAnyMessageSent, user?.user_id]);

    useEffect(() => {
        if (!coach || props.coach) return;
        setCoachRef.current(coach);
    }, [coach, props.coach]);

    useEffect(() => {
        if (!isAnyMessageSent || props.anyMessageSent) return;
        setAnyMessageSentRef.current(isAnyMessageSent);
    }, [isAnyMessageSent, props.anyMessageSent]);

    const getChannel = async (user: SendbirdUser): Promise<GroupChannel> => {
        if (channel) {
            getCoach(channel);
            return channel;
        }
        if (!sb) throw new Error("Sendbird instance is not initialized");
        await sb.connect(user.user_id, user.access_token);
        const ch = await sb.groupChannel.getChannel(user.channel_url);
        try {
            await ch.getMetaData(['support_user_id', 'subscriber_user_id']);
        } catch {
            // metadata fetch is non-critical
        }
        setChannel(ch);
        getCoach(ch);
        return ch;
    }

    const getCoach = (currentChannel: GroupChannel): void => {
        if (props.coach || !currentChannel) return;
        const autoresponderUserId = "autoresponder";
        
        if (currentChannel?.members.length > 1) {
            const validMembers = currentChannel.members.filter(member => 
                member.state === MemberState.JOINED &&
                member.userId !== user?.user_id &&
                member.userId !== autoresponderUserId && 
                member.userId.startsWith('vip_support_') === false
            );
            
            if (validMembers.length > 0) {
                setCoach(validMembers[0]);
            }
        }
    }

    const sendUserMessage = async (user: SendbirdUser, message: string): Promise<any> => {
        const channel = await getChannel(user);
        return new Promise((resolve, reject) => {
            channel
                .sendUserMessage({ message })
                .onSucceeded((msg) => {
                    setIsAnyMessageSent(true);
                    resolve(msg as UserMessage);
                })
                .onFailed((err) => reject(err));
        });
    }

    const sendFileMessage = async (user: SendbirdUser, file: File): Promise<any> => {
        const channel = await getChannel(user);
        const isCoach = user.user_role === 'coach' || window.pgUser?.is_coach;
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        const metaKey = isCoach ? 'coach_video_num' : 'user_video_num';
        const fileArgs: Parameters<typeof channel.sendFileMessage>[0] = {
            file,
            thumbnailSizes: [
                { maxWidth: 200, maxHeight: 200 },
                { maxWidth: 480, maxHeight: 480 },
            ],
        };
        if (isVideo) {
            fileArgs.fileName = isCoach ? `Fix ${numberOfVideos}` : `Swing ${numberOfVideos}`;
        }
        return new Promise((resolve, reject) => {
            channel
            .sendFileMessage(fileArgs)
            .onSucceeded((msg) => {
                if (isVideo) {
                    setUserVideoNum(user!, metaKey, numberOfVideos + 1);
                }
                // images are sent as-is, no counter or meta update needed
                resolve(msg);
            })
            .onFailed((err) => reject(err));
        });
    };
    
    const updateUserMessage = async (user: SendbirdUser, messageId: number, newMessage: string): Promise<any> => {
        const channel = await getChannel(user);
        return new Promise((resolve, reject) => {
            channel
                .updateUserMessage(messageId, { message: newMessage })
                .then((msg) => {
                    // Update local state
                    setSendbirdMessages((prev) => 
                        prev.map((m) => m.id === messageId ? mapToChatMessage([msg])[0] : m)
                    );
                    resolve(msg as UserMessage);
                })
                .catch((err) => reject(err));
        });
    };

    const deleteMessage = async (user: SendbirdUser, message: BaseMessage): Promise<void> => {
        const channel = await getChannel(user);
        return new Promise((resolve, reject) => {
            channel
                .deleteMessage(message)
                .then(() => {
                    // Update local state
                    setSendbirdMessages((prev) => prev.filter((m) => m.id !== message.messageId));
                    resolve();
                })
                .catch((err) => reject(err));
        });
    };

    const mapToChatMessage = (messages: any[]): ChatMessage[] => {
        return messages.map(m => ({
            id: m.messageId,
            sender_id: m.sender?.userId || null,
            message: m.message || null,
            created_at: m.createdAt ? new Date(m.createdAt) : undefined,
            type: m.messageType || null,
            raw: m,
        }));
    };

    const getUserVideoNum = async (user: SendbirdUser, meta_key: string): Promise<number> => {
        const channel = await getChannel(user);
        try {
            if (channel && typeof channel.getMetaCounters === 'function') {
                const counters = await channel.getMetaCounters([meta_key]);
                if (counters && counters[meta_key] != null) return Number(counters[meta_key]) || 1;
            }
        } catch (e) {
            console.error("Error fetching meta counters:", e);
        }
        
        const data = await channel.getMetaData([meta_key]);
        return Number(data && data[meta_key]) || 1;
    };

    const getVideoMessages = async (user: SendbirdUser): Promise<FileMessage[]> => {
        const channel = await getChannel(user);
        const videos: FileMessage[] = [];
        const query = channel.createPreviousMessageListQuery({
            limit: 100,
            reverse: false,
            messageTypeFilter: MessageTypeFilter.FILE,
        });
        while (query.hasNext) {
            const batch = await query.load();
            videos.push(...(batch as FileMessage[]));
        }
        return videos;
    };

    const setUserVideoNum = async (user: SendbirdUser, meta_key: string, value: number): Promise<void> => {
        const channel = await getChannel(user);
        try {
            if (channel && typeof channel.updateMetaCounters === 'function') {
                // Use upsert mode to create if it doesn't exist, or update if it does
                await channel.updateMetaCounters({ [meta_key]: value }, true);
            } else {
                await channel.updateMetaData({ [meta_key]: String(value) });
            }
            setNumberOfVideos(value);
        } catch (e) {
            console.error("Error setting meta counter:", e);
            throw e;
        }
    };

    const markAsRead = async (): Promise<void> => {
        if (channel) {
            await channel.markAsRead();
        }
    };

    return { 
        user,
        channel,
        sendbirdMessages, 
        setSendbirdMessages, 
        sendUserMessage,
        sendFileMessage,
        updateUserMessage,
        deleteMessage, 
        numberOfVideos, 
        setUserVideoNum,
        getVideoMessages,
        markAsRead,
        userLoading, 
        userError  
    };
};